import React, { Suspense, lazy } from "react";
import { BrowserRouter, Switch } from "react-router-dom";

import { ToastContainer } from "react-toastify";
import ErrorBoundary from "../components/ErrorBoundary";
import ModuleErrorBoundary, { ModuleLoading } from "../components/ModuleErrorBoundary";

import LoggedInLayout from "../layout";
import Dashboard from "../pages/Dashboard/";
import Tickets from "../pages/Tickets/";
import Signup from "../pages/Signup/";
import Login from "../pages/Login/";
import Connections from "../pages/Connections/";
import Settings from "../pages/Settings/";
import Users from "../pages/Users";
import Contacts from "../pages/Contacts/";
import QuickAnswers from "../pages/QuickAnswers/";
import Queues from "../pages/Queues/";
import SuperAdminDashboard from "../pages/SuperAdminDashboard/";
import SuperAdminPanel from "../pages/SuperAdminPanel/";
import SuperAdminUsersManager from "../pages/SuperAdminUsersManager/";
import SuperAdminResellers from "../pages/SuperAdminResellers/";
import SuperAdminConnections from "../pages/SuperAdminConnections/";
import SuperAdminApis from "../pages/SuperAdminApis/";
import SuperAdminLogs from "../pages/SuperAdminLogs/";
import SuperAdminAnnouncements from "../pages/SuperAdminAnnouncements/";
import SuperAdminTurnosIA from "../pages/SuperAdminTurnosIA/";
import SuperAdminWhiteLabel from "../pages/SuperAdminWhiteLabel/";
import SuperAdminMatchProp from "../pages/SuperAdminMatchProp/";
import SuperAdminAI from "../pages/SuperAdminAI/";
import SuperAdminAIStats from "../pages/SuperAdminAIStats/";
import SuperAdminChecklist from "../pages/SuperAdminChecklist/";
import SuperAdminAtencion from "../pages/SuperAdminAtencion/";
import SuperAdminChangelog from "../pages/SuperAdminChangelog/";
import SuperAdminDocs from "../pages/SuperAdminDocs/";
import Stats from "../pages/Stats/";
import Tags from "../pages/Tags/";
import Reports from "../pages/Reports/";
import PendingDashboard from "../pages/PendingDashboard/";
import ContactStatesAdmin from "../pages/ContactStatesAdmin/";
import CRMBoard from "../components/CRMBoard/";
import Support from "../pages/Support";
import MatchPropAdmin from "../pages/MatchPropAdmin/";
import AIServices from "../pages/AIServices/";
import AIAssistantStats from "../pages/AIAssistantStats/";
import AppointmentsSettings from "../pages/AppointmentsSettings/";
// TurnosIA se carga DIFERIDO a propósito (ver ModuleErrorBoundary):
//  - un error al bajar el chunk queda contenido por el boundary de abajo en
//    vez de romper el bundle principal;
//  - WAIA no paga el peso del módulo hasta que alguien entra a /turnos.
const TurnosIA = lazy(() => import("../pages/TurnosIA"));
import CargaDeDatos from "../pages/CargaDeDatos";
import SalaEspera from "../pages/SalaEspera";
import SalaEsperaShort from "../pages/SalaEspera/SalaEsperaShort";
import { AuthProvider } from "../context/Auth/AuthContext";
import { WhatsAppsProvider } from "../context/WhatsApp/WhatsAppsContext";
import { ThemeProvider } from "../context/DarkMode";
import { SettingsProvider } from "../context/Settings/SettingsContext";
import { UiPrefsProvider } from "../context/UiPrefs";
import { FloatingChatProvider } from "../context/FloatingChat";
import Route from "./Route";

const Routes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <Switch>
            <Route exact path="/login" component={Login} />
            <Route exact path="/signup" component={Signup} />
            <Route exact path="/sala/:companySlug/:roomSlug" component={SalaEspera} />
            <Route exact path="/s/:code" component={SalaEsperaShort} />
            <SettingsProvider>
            <WhatsAppsProvider>
              <UiPrefsProvider>
              <FloatingChatProvider>
              <LoggedInLayout>
                <ErrorBoundary>
                <Route exact path="/" component={Reports} isPrivate />
                <Route exact path="/dashboard" component={Reports} isPrivate />
                <Route exact path="/announcements" component={Dashboard} isPrivate />
                <Route exact path="/tickets/:ticketId?" component={Tickets} isPrivate />
                <Route exact path="/connections" component={Connections} isPrivate />
                <Route exact path="/contacts" component={Contacts} isPrivate />
                <Route exact path="/users" component={Users} isPrivate />
                <Route exact path="/quickAnswers" component={QuickAnswers} isPrivate />
                <Route exact path="/Settings" component={Settings} isPrivate />
                <Route exact path="/Queues" component={Queues} isPrivate />
                <Route exact path="/superadmin" component={SuperAdminPanel} isPrivate />
                <Route exact path="/superadmin/users" component={SuperAdminUsersManager} isPrivate />
                <Route exact path="/superadmin/resellers" component={SuperAdminResellers} isPrivate />
                <Route exact path="/superadmin/dashboard" component={SuperAdminDashboard} isPrivate />
                <Route exact path="/superadmin/connections" component={SuperAdminConnections} isPrivate />
                <Route exact path="/superadmin/apis" component={SuperAdminApis} isPrivate />
                <Route exact path="/superadmin/logs" component={SuperAdminLogs} isPrivate />
                <Route exact path="/superadmin/announcements" component={SuperAdminAnnouncements} isPrivate />
                <Route exact path="/superadmin/turnosIA" component={SuperAdminTurnosIA} isPrivate />
                <Route exact path="/superadmin/changelog" component={SuperAdminChangelog} isPrivate />
                <Route exact path="/superadmin/docs" component={SuperAdminDocs} isPrivate />
                <Route exact path="/superadmin/whitelabel" component={SuperAdminWhiteLabel} isPrivate />
                <Route exact path="/superadmin/matchprop" component={SuperAdminMatchProp} isPrivate />
                <Route exact path="/superadmin/ai" component={SuperAdminAI} isPrivate />
                <Route exact path="/superadmin/ai-stats" component={SuperAdminAIStats} isPrivate />
                <Route exact path="/superadmin/checklist" component={SuperAdminChecklist} isPrivate />
                {/* feat_407 — Bandeja de Atención de Soporte (staff). Boundary propio: un crash
                    acá no tumba el resto del panel. */}
                <ModuleErrorBoundary module="Soporte/atencion">
                  <Route exact path="/superadmin/atencion" component={SuperAdminAtencion} isPrivate />
                </ModuleErrorBoundary>
                <Route exact path="/stats" component={Stats} isPrivate />
                <Route exact path="/tags" component={Tags} isPrivate />
                <Route exact path="/pending-dashboard" component={PendingDashboard} isPrivate />
                <Route exact path="/contact-states-admin" component={ContactStatesAdmin} isPrivate />
                <Route exact path="/crm" component={CRMBoard} isPrivate />
                <ModuleErrorBoundary module="Soporte">
                  <Route exact path="/soporte" component={Support} isPrivate />
                </ModuleErrorBoundary>
                <Route exact path="/matchprop-admin" component={MatchPropAdmin} isPrivate />
                <Route exact path="/ai-services" component={AIServices} isPrivate />
                <Route exact path="/ai-assistant-stats" component={AIAssistantStats} isPrivate />
                <Route exact path="/appointments-settings" component={AppointmentsSettings} isPrivate />
                <ModuleErrorBoundary module="TurnosIA">
                  <Suspense fallback={<ModuleLoading />}>
                    <Route exact path="/turnos" component={TurnosIA} isPrivate />
                  </Suspense>
                </ModuleErrorBoundary>
                <Route exact path="/carga-de-datos" component={CargaDeDatos} isPrivate />
                </ErrorBoundary>
              </LoggedInLayout>
              </FloatingChatProvider>
              </UiPrefsProvider>
            </WhatsAppsProvider>
            </SettingsProvider>
          </Switch>
          <ToastContainer autoClose={3000} />
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
